// src/page/PackageNew.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import { TICKET_GENRE_OPTIONS } from '../constants/ticketOptions';
import { AREA_OPTIONS } from '../constants/spotOptions';

export default function PackageNew() {
    useAuthGuard();
    const navigate = useNavigate();

    // ---------- helpers ----------
    const pad = (n) => String(n).padStart(2, '0');
    const fmtDate = (d) =>
        `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    const toISOorNull = (d) => (d instanceof Date && !isNaN(d) ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString() : null);

    // Accept "~", "-", " - ", or "から"
    const splitRange = (s) => {
        if (!s) return ['', ''];
        const SEP = /\s*(~|から|-|to)\s*/i;
        const parts = s.split(SEP).filter(Boolean);
        const [a, b] = parts.length === 3 ? [parts[0], parts[2]] : [parts[0], parts[1]];
        return [a?.trim() ?? '', b?.trim() ?? ''];
    };

    // ---------- states ----------
    const [packageName, setPackageName] = useState('');
    const [spotId, setSpotId] = useState('');
    const [spots, setSpots] = useState([]);
    const [loadingSpots, setLoadingSpots] = useState(false);

    const [displayOrder, setDisplayOrder] = useState('');
    const [priceAdult, setPriceAdult] = useState('');
    const [priceChild, setPriceChild] = useState('');

    const [publishedFlag, setPublishedFlag] = useState(null);  // true/false/null
    const [userCancelable, setUserCancelable] = useState(null); // true/false/null

    const [refundFee, setRefundFee] = useState('');
    const [cancelFee, setCancelFee] = useState('');

    const [ticketArea, setTicketArea] = useState('');
    const [genres, setGenres] = useState([]);

    // NEW fields
    const [ticketType, setTicketType] = useState('');
    const [howToUse, setHowToUse] = useState('');
    const [termsOfUse, setTermsOfUse] = useState('');
    const [caution, setCaution] = useState('');
    const [ticketImage, setTicketImage] = useState(null); // (upload API if needed)

    const [salesRange, setSalesRange] = useState('');
    const [publishRange, setPublishRange] = useState('');

    // ---- vouchers UI state ----
    const [vouchers, setVouchers] = useState([]);
    const [loadingVouchers, setLoadingVouchers] = useState(false);
    const [selectedVoucherId, setSelectedVoucherId] = useState('');
    // [{voucherId, voucherName, bindingNumber, displayOrder}]
    const [boundVouchers, setBoundVouchers] = useState([]);

    const handleAddVoucher = () => {
        if (!selectedVoucherId) return;
        if (boundVouchers.some(b => b.voucherId === selectedVoucherId)) return;
        const v = vouchers.find(x => x.voucherId === selectedVoucherId);
        if (!v) return;
        const nextOrder = (boundVouchers[boundVouchers.length - 1]?.displayOrder ?? 0) + 1;
        setBoundVouchers(prev => [...prev, {
            voucherId: v.voucherId,
            voucherName: v.voucherName,
            bindingNumber: 1,
            displayOrder: nextOrder
        }]);
        setSelectedVoucherId('');
    };
    const handleChangeBinding = (voucherId, value) => {
        const n = Math.max(1, parseInt(value || '1', 10) || 1);
        setBoundVouchers(prev => prev.map(row => row.voucherId === voucherId ? { ...row, bindingNumber: n } : row));
    };
    const handleDeleteVoucher = (voucherId) => {
        setBoundVouchers(prev => prev.filter(row => row.voucherId !== voucherId));
    };

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // pickers (ID重複を前提に、順番で紐付け)
    const salesPickerRef = useRef(null);
    const publishPickerRef = useRef(null);

    useEffect(() => {
        if (!window.flatpickr) return;
        const inputs = Array.from(document.querySelectorAll('#public_range'));
        const ja = window.flatpickr.l10ns?.ja || {};
        if (inputs[0]) {
            salesPickerRef.current = window.flatpickr(inputs[0], {
                mode: 'range',
                enableTime: true,
                time_24hr: true,
                dateFormat: 'Y/m/d H:i',
                locale: { ...ja, rangeSeparator: ' ~ ' },
            });
        }
        if (inputs[1]) {
            publishPickerRef.current = window.flatpickr(inputs[1], {
                mode: 'range',
                enableTime: true,
                time_24hr: true,
                dateFormat: 'Y/m/d H:i',
                locale: { ...ja, rangeSeparator: ' ~ ' },
            });
        }
        return () => {
            try { salesPickerRef.current?.destroy(); } catch { }
            try { publishPickerRef.current?.destroy(); } catch { }
        };
    }, []);

    // reflect state -> pickers
    useEffect(() => {
        if (!salesPickerRef.current) return;
        const [a, b] = splitRange(salesRange);
        if (a && b) salesPickerRef.current.setDate([a, b], true);
    }, [salesRange]);
    useEffect(() => {
        if (!publishPickerRef.current) return;
        const [a, b] = splitRange(publishRange);
        if (a && b) publishPickerRef.current.setDate([a, b], true);
    }, [publishRange]);

    // load spots
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoadingSpots(true);
                const token = localStorage.getItem('access_token');
                const res = await fetch(`${API_BASE_URL}/v1/spot?page=1&size=200`, {
                    headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
                    signal: ac.signal
                });
                const data = await res.json().catch(() => ({}));
                const list = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
                const mapped = list.map(s => ({
                    spotId: s.spotId ?? s.id ?? s.spot_id ?? '',
                    spotName: s.spotName ?? s.name ?? s.spot_name ?? ''
                })).filter(x => x.spotId && x.spotName);
                setSpots(mapped);
            } catch {
                setSpots([]);
            } finally {
                setLoadingSpots(false);
            }
        })();
        return () => ac.abort();
    }, []);

    // load vouchers
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoadingVouchers(true);
                const token = localStorage.getItem('access_token');
                const res = await fetch(`${API_BASE_URL}/v1/voucher?page=1&size=200`, {
                    headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
                    signal: ac.signal
                });
                const data = await res.json().catch(() => ({}));
                const list = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
                const mapped = list.map(v => ({
                    voucherId: v.voucherId ?? v.id ?? v.voucher_id ?? '',
                    voucherName: v.voucherName ?? v.name ?? v.voucher_name ?? ''
                })).filter(x => x.voucherId && x.voucherName);
                setVouchers(mapped);
            } catch {
                setVouchers([]);
            } finally {
                setLoadingVouchers(false);
            }
        })();
        return () => ac.abort();
    }, []);

    // ---------- submit ----------
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;

        const errs = [];
        if (!(packageName || '').trim()) errs.push('パッケージ名を入力してください。');
        if (!spotId) errs.push('スポット名を選択してください。');

        const nAdult = Number(priceAdult);
        if (!Number.isFinite(nAdult) || nAdult < 0) errs.push('大人の金額は0以上の数値で入力してください。');

        const nChild = Number(priceChild);
        if (!Number.isFinite(nChild) || nChild < 0) errs.push('小人の金額は0以上の数値で入力してください。');

        const nRefund = refundFee === '' ? 0 : Number(refundFee);
        if (!Number.isFinite(nRefund) || nRefund < 0) errs.push('払い戻し手数料は0以上の数値で入力してください。');

        const nCancel = cancelFee === '' ? 0 : Number(cancelFee);
        if (!Number.isFinite(nCancel) || nCancel < 0) errs.push('取り消し手数料は0以上の数値で入力してください。');

        if (publishedFlag === null) errs.push('公開設定（非公開/公開）を選択してください。');
        if (userCancelable === null) errs.push('ユーザーキャンセルフラグを選択してください。');

        // ranges
        const inputs = Array.from(document.querySelectorAll('#public_range'));
        const salesVal = inputs[0]?.value || salesRange;
        const publishVal = inputs[1]?.value || publishRange;

        const parseRange = (s) => {
            const [sa, sb] = splitRange(s);
            const da = new Date(sa), db = new Date(sb);
            if (!sa || !sb || isNaN(da) || isNaN(db) || da > db) return [null, null];
            return [da, db];
        };

        const [saleA, saleB] = parseRange(salesVal);
        const [pubA, pubB] = parseRange(publishVal);

        if (!saleA || !saleB) errs.push('販売期間を「YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm」で正しく入力してください。');
        if (!pubA || !pubB) errs.push('公開期間を「YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm」で正しく入力してください。');

        // vouchers client-side validation (optional)
        for (const r of boundVouchers) {
            if (!r.voucherId) { errs.push('バウチャーIDが不正です。'); break; }
            if (!Number.isFinite(r.bindingNumber) || r.bindingNumber < 1) { errs.push('綴り枚数は1以上にしてください。'); break; }
        }

        if (errs.length) {
            alert(errs.join('\n'));
            return;
        }

        const payload = {
            packageName: packageName.trim(),
            spotId,
            displayOrder: displayOrder === '' ? null : Number(displayOrder),
            pricePackageAdult: nAdult,
            pricePackageChild: nChild,
            publishedFlag: !!publishedFlag,
            salesStartDate: toISOorNull(saleA),
            salesEndDate: toISOorNull(saleB),
            publishedStartDate: toISOorNull(pubA),
            publishedEndDate: toISOorNull(pubB),
            refundFeePerUser: nRefund,
            cancellationFeePerUser: nCancel,
            userCancelable: !!userCancelable,
            ticketArea,
            ticketGenre: genres.join(','),

            // NEW fields
            ticketPackageType: ticketType || undefined,
            howToUse: howToUse || undefined,
            termsOfUse: termsOfUse || undefined,
            caution: caution || undefined,

            // VOUCHERS payload
            vouchers: boundVouchers.map((r, idx) => ({
                voucherId: r.voucherId,
                bindingNumber: Number(r.bindingNumber) || 1,
                displayOrder: Number.isFinite(Number(r.displayOrder)) ? Number(r.displayOrder) : idx + 1,
            })),
        };

        try {
            setSaving(true);
            const token = localStorage.getItem('access_token');
            const res = await fetch(`${API_BASE_URL}/v1/package`, {
                method: 'POST',
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                let msg = `登録に失敗しました (HTTP ${res.status})`;
                try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
                throw new Error(msg);
            }
            alert('登録しました。');
            navigate('/package');
        } catch (err) {
            alert(err.message || '登録に失敗しました。');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout>
            <>
                <nav className="mb-3" aria-label="breadcrumb">
                    <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item"><a href="#!">デジチケ管理</a></li>
                        <li className="breadcrumb-item active">新規登録</li>
                    </ol>
                </nav>

                <form className="mb-9" onSubmit={handleSubmit}>
                    <div className="row g-3 flex-between-end mb-5">
                        <div className="col-auto">
                            <h2 className="mb-2">パッケージ登録</h2>
                            <h5 className="text-body-tertiary fw-semibold">{/* */}</h5>
                        </div>
                        <div className="col-auto">
                            <button
                                className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0"
                                type="button"
                                onClick={() => window.history.back()}
                                disabled={saving}
                            >キャンセル</button>
                            <button
                                className="btn btn-primary mb-2 mb-sm-0"
                                type="submit"
                                disabled={saving}
                            >{saving ? '保存中...' : '登録'}</button>
                        </div>
                    </div>

                    <div className="row g-5">
                        <div className="col-12 col-xl-8">

                            <div className="mb-4">
                                <h4 className="mb-3">パッケージ名<span className="text-danger">*</span></h4>
                                <input
                                    className="form-control"
                                    type="text"
                                    name="package_name"
                                    value={packageName}
                                    required
                                    onChange={(e) => setPackageName(e.target.value)}
                                />
                            </div>

                            <div className="mb-4">
                                <h4 className="mb-3">スポット名<span className="text-danger">*</span></h4>
                                <div className="input-group">
                                    <select
                                        className="form-control"
                                        value={spotId}
                                        onChange={(e) => setSpotId(e.target.value)}
                                        disabled={loadingSpots}
                                        required
                                    >
                                        <option value="">{loadingSpots ? '読み込み中...' : '選択してください'}</option>
                                        {spots.map(s => (
                                            <option key={s.spotId} value={s.spotId}>{s.spotName}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* 券種 */}
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">券種</h4>
                                        <input
                                            className="form-control"
                                            type="text"
                                            name="package_category"
                                            value={ticketType}
                                            onChange={(e) => setTicketType(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* バウチャー */}
                            <div className="mb-4">
                                <h4 className="mb-3">バウチャー</h4>
                                <div className="input-group">
                                    <select
                                        className="form-control"
                                        value={selectedVoucherId}
                                        onChange={(e) => setSelectedVoucherId(e.target.value)}
                                    >
                                        <option value="">{loadingVouchers ? '読み込み中...' : '選択してください'}</option>
                                        {vouchers.map(v => (
                                            <option key={v.voucherId} value={v.voucherId}>{v.voucherName}</option>
                                        ))}
                                    </select>
                                    <button
                                        className="btn btn-outline-secondary"
                                        type="button"
                                        onClick={handleAddVoucher}
                                        disabled={!selectedVoucherId}
                                    >＋</button>
                                </div>

                                <table className="table fs-9 mb-0 mt-3">
                                    <thead>
                                        <tr>
                                            <th className="align-middle ps-3" style={{ width: 40 }}></th>
                                            <th className="align-middle ps-3">バウチャーID</th>
                                            <th className="align-middle ps-3">バウチャー名</th>
                                            <th className="align-middle ps-3" style={{ width: 140 }}>綴り枚数</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {boundVouchers.length === 0 ? (
                                            <tr><td className="ps-3" colSpan={4}>未追加です</td></tr>
                                        ) : (
                                            boundVouchers.map(row => (
                                                <tr key={row.voucherId}>
                                                    <td className="ps-3">
                                                        <button
                                                            type="button"
                                                            className="btn btn-link text-danger p-0"
                                                            title="削除"
                                                            aria-label="削除"
                                                            onClick={() => handleDeleteVoucher(row.voucherId)}
                                                            style={{ fontSize: 18, lineHeight: 1, textDecoration: 'none' }}
                                                        >×</button>
                                                    </td>
                                                    <td className="ps-3">{row.voucherId}</td>
                                                    <td className="ps-3">{row.voucherName}</td>
                                                    <td className="ps-3">
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            className="form-control form-control-sm"
                                                            value={row.bindingNumber}
                                                            onChange={(e) => handleChangeBinding(row.voucherId, e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* 価格 */}
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">大人<span className="text-danger">*</span></h4>
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="input-group">
                                                <input
                                                    className="form-control"
                                                    type="number"
                                                    name="ticket_price_adult"
                                                    min="0"
                                                    value={priceAdult}
                                                    required
                                                    onChange={(e) => setPriceAdult(e.target.value)}
                                                />
                                                <span className="input-group-text">円</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-4 col-md-6">
                                    <h4 className="mb-3">小人<span className="text-danger">*</span></h4>
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="input-group">
                                            <input
                                                className="form-control"
                                                type="number"
                                                name="ticket_price_child"
                                                min="0"
                                                value={priceChild}
                                                required
                                                onChange={(e) => setPriceChild(e.target.value)}
                                            />
                                            <span className="input-group-text">円</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 手数料 */}
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">払い戻し手数料<span className="text-danger">*</span></h4>
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="input-group">
                                                <input
                                                    className="form-control"
                                                    type="number"
                                                    name="ticket_refused_fee"
                                                    min="0"
                                                    value={refundFee}
                                                    required
                                                    onChange={(e) => setRefundFee(e.target.value)}
                                                />
                                                <span className="input-group-text">円</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-4 col-md-6">
                                    <h4 className="mb-3">取り消し手数料<span className="text-danger">*</span></h4>
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="input-group">
                                            <input
                                                className="form-control"
                                                type="number"
                                                name="ticket_canceled_fee"
                                                min="0"
                                                value={cancelFee}
                                                required
                                                onChange={(e) => setCancelFee(e.target.value)}
                                            />
                                            <span className="input-group-text">円</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ユーザーキャンセル */}
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">ユーザーキャンセルフラグ<span className="text-danger">*</span></h4>
                                        <div className="d-flex align-items-center gap-3">
                                            <label className="mb-0">
                                                <input
                                                    type="radio"
                                                    name="ticket_user_cancel_flag"
                                                    value="0"
                                                    checked={userCancelable === false}
                                                    onChange={() => setUserCancelable(false)}
                                                    required
                                                /> 否
                                            </label>
                                            <label className="mb-0">
                                                <input
                                                    type="radio"
                                                    name="ticket_user_cancel_flag"
                                                    value="1"
                                                    checked={userCancelable === true}
                                                    onChange={() => setUserCancelable(true)}
                                                /> 可
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-4 col-md-6" />
                            </div>

                            {/* テキスト */}
                            <div className="mb-4">
                                <h4 className="mb-3">使用方法</h4>
                                <textarea
                                    className="form-control"
                                    name="how_to_use"
                                    rows={4}
                                    value={howToUse}
                                    onChange={(e) => setHowToUse(e.target.value)}
                                ></textarea>
                            </div>
                            <div className="mb-4">
                                <h4 className="mb-3">利用条件</h4>
                                <textarea
                                    className="form-control"
                                    name="terms_of_use"
                                    rows={4}
                                    value={termsOfUse}
                                    onChange={(e) => setTermsOfUse(e.target.value)}
                                ></textarea>
                            </div>
                            <div className="mb-4">
                                <h4 className="mb-3">注意事項</h4>
                                <textarea
                                    className="form-control"
                                    name="caution"
                                    rows={4}
                                    value={caution}
                                    onChange={(e) => setCaution(e.target.value)}
                                ></textarea>
                            </div>

                            {/* エリア */}
                            <div className="mb-4">
                                <h4 className="mb-3">エリア<span className="text-danger">*</span></h4>
                                <select
                                    className="form-control"
                                    name="area"
                                    value={ticketArea}
                                    required
                                    onChange={(e) => setTicketArea(e.target.value)}
                                >
                                    <option value="">選択してください</option>
                                    {AREA_OPTIONS.map(o => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </div>

                        </div>

                        <div className="col-12 col-xl-4">
                            <div className="row g-2">
                                <div className="col-12 col-xl-12">
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="row gx-3">
                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <h5 className="mb-3">公開設定<span className="text-danger">*</span></h5>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <label className="mb-0">
                                                                <input
                                                                    type="radio"
                                                                    name="publish_status"
                                                                    value="0"
                                                                    checked={publishedFlag === false}
                                                                    onChange={() => setPublishedFlag(false)}
                                                                    required
                                                                /> 非公開
                                                            </label>
                                                            <label className="mb-0">
                                                                <input
                                                                    type="radio"
                                                                    name="publish_status"
                                                                    value="1"
                                                                    checked={publishedFlag === true}
                                                                    onChange={() => setPublishedFlag(true)}
                                                                /> 公開
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* 販売期間 */}
                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <div className="d-flex flex-wrap mb-2">
                                                            <h5 className="mb-0 text-body-highlight me-2">販売期間<span className="text-danger">*</span></h5>
                                                        </div>
                                                        <input
                                                            className="form-control"
                                                            type="text"
                                                            name="public_range"
                                                            id="public_range"
                                                            placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                                                            value={salesRange}
                                                            required
                                                            onChange={(e) => setSalesRange(e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* 公開期間 */}
                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <div className="d-flex flex-wrap mb-2">
                                                            <h5 className="mb-0 text-body-highlight me-2">公開期間<span className="text-danger">*</span></h5>
                                                        </div>
                                                        <input
                                                            className="form-control"
                                                            type="text"
                                                            name="public_range"
                                                            id="public_range"
                                                            placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                                                            value={publishRange}
                                                            required
                                                            onChange={(e) => setPublishRange(e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* 表示順（任意） */}
                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <div className="d-flex flex-wrap mb-2">
                                                            <h5 className="mb-0 text-body-highlight me-2">表示順</h5>
                                                        </div>
                                                        <input
                                                            className="form-control"
                                                            type="number"
                                                            name="order"
                                                            value={displayOrder}
                                                            onChange={(e) => setDisplayOrder(e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* ジャンル */}
                                <div className="col-12 col-xl-12">
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="row gx-3">
                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <div className="d-flex flex-wrap mb-2">
                                                            <h5 className="mb-0 text-body-highlight me-2">ジャンル<span className="text-danger">*</span></h5>
                                                        </div>
                                                        {TICKET_GENRE_OPTIONS.map(([value, label]) => (
                                                            <label key={value} className="me-3">
                                                                <input
                                                                    type="checkbox"
                                                                    name="genre[]"
                                                                    value={value}
                                                                    checked={genres.includes(value)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) setGenres([...genres, value]);
                                                                        else setGenres(genres.filter(g => g !== value));
                                                                    }}
                                                                /> {label}
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* 画像 */}
                                <div className="col-12 col-xl-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <h4 className="card-title mb-4">パッケージ画像</h4>
                                            <div
                                                className="dropzone dropzone-multiple p-0 mb-5"
                                                id="my-awesome-dropzone"
                                                data-dropzone="data-dropzone"
                                            >
                                                <div className="fallback">
                                                    <input
                                                        name="file"
                                                        type="file"
                                                        multiple
                                                        onChange={(e) => setTicketImage(e.target.files?.[0] || null)}
                                                    />
                                                </div>
                                                <div className="dz-preview d-flex flex-wrap"></div>
                                                <div className="dz-message text-body-tertiary text-opacity-85" data-dz-message="data-dz-message">
                                                    Drag your photo here<span className="text-body-secondary px-1">or</span>
                                                    <button className="btn btn-link p-0" type="button">Browse from device</button><br />
                                                    <img className="mt-3 me-2" src="../../../assets/img/icons/image-icon.png" width="40" alt="" />
                                                </div>
                                            </div>
                                            {error && <div className="text-danger mt-3">{error}</div>}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </form>
            </>
        </Layout>
    );
}
